import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { Section } from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { RelativeTime } from '~/elements';
import { EventRepository, parseEvent } from '~/repositories';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { EventEvent } from '~/typings/API';
import { formatString } from '~/util';

interface Params {
  id: string;
}

interface EventRow {
  id: string;
  eventType: string;
  createdAt: string;
  data: any;
}

const getCardAccountEventLabel = (eventType: string): string => {
  const parts = eventType.split('.');
  const finalPart = parts[parts.length - 1] || eventType;
  return formatString(finalPart);
};

const columns: TableColumn<EventRow>[] = [
  {
    header: 'Event',
    accessor: 'eventType',
    width: { min: 200, max: '3fr' },
    cell: ({ value }) => <Table.TextCell value={getCardAccountEventLabel(value ?? '')} weight="medium" />,
  },
  {
    header: 'Date',
    accessor: 'createdAt',
    width: { min: 140, max: '1fr' },
    cell: ({ value }) => (value ? <RelativeTime timestamp={value} /> : <Table.TextCell value="-" />),
  },
];

export const PageCardAccountEvents: React.FC = () => {
  const { id: cardAccountId } = useParams<keyof Params>() as Params;
  const { currentPlatform, isSandbox } = useSessionStore();
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['card-account-events', cardAccountId, currentPlatform?.id, isSandbox],
    queryFn: () =>
      EventRepository.getAll({
        objectIds: [cardAccountId],
      } as any),
    enabled: !!cardAccountId,
  });

  const eventRows: EventRow[] = (eventsData?.events ?? []).map((event: EventEvent) => {
    const parsed = parseEvent(event);
    return {
      id: parsed.id,
      eventType: parsed.eventType,
      createdAt: parsed.createdAt,
      data: event.data,
    };
  });

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <Inner p={0}>
        <Section headline="Events">
          <Inner px="var(--page-margin)" py={0}>
            <Table<EventRow>
              columns={columns}
              data={eventRows}
              isLoading={isLoading}
              variant={TableVariant.List}
              onRowClick={(row) => {
                setSelectedEventData(row.data);
                setIsEventDetailModalOpen(true);
              }}
            />
          </Inner>
        </Section>
      </Inner>
    </>
  );
};
